import type { BookingDelivery } from "@/api/types";
import { cn } from "@/lib/utils";

/**
 * Where auto-booked meetings go: one sideways switch with a segment per destination.
 *
 * <p>Both destinations are named on the control itself. Neither is implied by the absence of the
 * other, which is what rules out an on/off toggle here: "off" would never say that meetings arrive
 * by email, and saying it underneath would put back the small print this control exists to remove.
 * Two named segments say it without a caption.</p>
 *
 * <h3>Choice and action are separate things</h3>
 *
 * <p>An earlier version was a two-position pill whose right half turned into a "connect a calendar"
 * link when there was no calendar. That is a category error: the two positions of a switch are two
 * states of one setting, not one state and one link. It also read as broken, because the link only
 * scrolled to a panel that was usually already on screen.</p>
 *
 * <p>Sentence case, unlike the section headings above it. These are values a host reads and picks
 * between, not a name for the section; setting them in caps made two short phrases into two more
 * headings and cost the legibility the muted half needs most.</p>
 *
 * <p>The switch is back, and that mistake is not, because there is no longer a second control here
 * to make it with. When there is no calendar the second segment is present but not selectable: a
 * destination that exists and cannot be honoured yet, named at ordinary muted contrast rather than
 * faded out, with the prerequisite in its tooltip.</p>
 *
 * <h3>Connecting is not this control's job, but pointing at it is</h3>
 *
 * <p>There was a connect button on this row, a filled CTA sitting a few hundred pixels from the
 * Connected calendars panel that exists to do exactly that: two competing calls to action for one
 * task, the louder attached to a setting rather than to the thing it set up. The panel owns
 * connecting, and it still does.</p>
 *
 * <p>What replaced the button is the second half of the switch itself. Locked, it reads
 * <em>Connect a calendar</em> — the thing that has to happen before the destination exists — and
 * pressing it reveals the panel rather than starting consent. It does not set the value, and it
 * never claims to: the switch has one working position until a calendar exists.</p>
 *
 * <p><b>Why revealing is not the old mistake.</b> A link that scrolled to a panel usually already
 * on screen was once removed from here for looking like it did nothing. The fix is not to avoid
 * pointing at the panel, it is to make the pointing visible: {@code onRevealCalendars} rings the
 * panel as well as scrolling to it, so the answer to "what was that about" is on screen even when
 * no scrolling was needed.</p>
 *
 * <p>The host is not stranded either way: connecting in the panel brings them back to this control,
 * scrolled to and ringed ({@code highlight}), because it is the setting that changed underneath
 * them.</p>
 */
export function DeliveryOptions({
  value,
  locked,
  disabled,
  highlight,
  onChange,
  onRevealCalendars,
}: {
  /** The route a meeting would really take, which is email whenever `locked`. */
  value: BookingDelivery;
  /** No calendar to write to: the calendar segment is shown but cannot be chosen. */
  locked: boolean;
  disabled?: boolean;
  /** Ring the control after a consent round trip, so the eye finds what just changed. */
  highlight?: boolean;
  onChange: (next: BookingDelivery) => void;
  /** Show the host the Connected calendars panel. Reveals it; does not start consent. */
  onRevealCalendars: () => void;
}) {
  return (
    <div
      // Only a radiogroup when there is something to choose. Locked, it holds one radio and one
      // button, and calling that a group of options would misdescribe it to a screen reader.
      role={locked ? undefined : "radiogroup"}
      aria-label="Where auto-booked meetings go"
      className={cn(
        "inline-flex overflow-hidden rounded-full border transition",
        highlight ? "border-brand-blue ring-2 ring-brand-blue/40" : "border-border",
        disabled && "opacity-50",
      )}
    >
      <DeliverySegment
        checked={value === "EMAIL"}
        disabled={disabled}
        onSelect={() => onChange("EMAIL")}
        label="Email me"
      />
      {locked ? (
        // Named for the errand, not the destination. "Add to my calendar" as a dead half described
        // something the host could not have; "Connect a calendar" describes the one thing standing
        // between them and it, which is also what pressing it explains.
        //
        // A button, not a radio: it sets nothing. `muted-foreground`, not `/60` — faded out it read
        // as broken rather than as pending, and at that opacity it failed contrast here. It takes
        // the same hover as an unselected segment, because it is pressable and should look it.
        <button
          type="button"
          onClick={onRevealCalendars}
          disabled={disabled}
          className={cn(
            "cursor-pointer border-l border-border px-3 py-1.5 font-comic-headline text-sm",
            "text-muted-foreground transition hover:bg-brand-blue/10 hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            "disabled:cursor-not-allowed",
          )}
          title="Show me the Connected calendars panel"
        >
          Connect a calendar
        </button>
      ) : (
        <DeliverySegment
          checked={value === "CALENDAR"}
          disabled={disabled}
          onSelect={() => onChange("CALENDAR")}
          label="Add to my calendar"
          divider
        />
      )}
    </div>
  );
}

/**
 * One segment of the switch. A real radio input, so arrow keys, roving focus and the accessibility
 * tree come from the platform rather than from a pile of key handlers. The whole segment is the
 * label, so the hit target is the segment and not a dot.
 */
function DeliverySegment({
  checked,
  disabled,
  onSelect,
  label,
  divider,
}: {
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
  label: string;
  /** Rule between the segments. Only the right-hand one draws it, so it is never doubled. */
  divider?: boolean;
}) {
  return (
    <label
      className={cn(
        "cursor-pointer px-3 py-1.5 font-comic-headline text-sm transition",
        "focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring",
        divider && "border-l border-border",
        checked
          ? "bg-brand-blue text-brand-blue-foreground"
          : "text-muted-foreground hover:bg-brand-blue/10 hover:text-foreground",
        disabled && "cursor-not-allowed",
      )}
    >
      <input
        type="radio"
        name="booking-delivery"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      {label}
    </label>
  );
}
